import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import {
  type MeasurementItem,
  type MeasurementTypeFilter,
  type CreateMeasurementData,
  type UpdateMeasurementData,
} from '../hooks/use-measurements'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface MeasurementFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  measurement?: MeasurementItem | null
  defaultType?: MeasurementTypeFilter
  onCreate: (data: CreateMeasurementData) => Promise<void>
  onUpdate: (data: UpdateMeasurementData) => Promise<void>
}

function localInputFromIso(iso: string): string {
  const date = new Date(iso)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

export function MeasurementFormDialog({
  open,
  onOpenChange,
  measurement,
  defaultType = 'body_weight',
  onCreate,
  onUpdate,
}: MeasurementFormDialogProps) {
  const [measurementType, setMeasurementType] = useState<MeasurementTypeFilter>(defaultType)
  const [measuredAt, setMeasuredAt] = useState('')
  const [notes, setNotes] = useState('')
  const [weight, setWeight] = useState('')
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lb'>('kg')
  const [systolic, setSystolic] = useState('')
  const [diastolic, setDiastolic] = useState('')
  const [pulse, setPulse] = useState('')
  const [bpm, setBpm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (measurement) {
      const t = measurement.measurement_type
      if (t === 'body_weight' || t === 'blood_pressure' || t === 'heart_rate') {
        setMeasurementType(t)
      }
      setMeasuredAt(localInputFromIso(measurement.measured_at))
      setNotes(measurement.notes || '')
      if (measurement.measurement_type === 'body_weight' && measurement.mass_kg) {
        setWeight(measurement.mass_kg)
        setWeightUnit('kg')
      } else {
        setWeight('')
        setWeightUnit('kg')
      }
      if (measurement.measurement_type === 'blood_pressure') {
        setSystolic(String(measurement.systolic_mmhg ?? ''))
        setDiastolic(String(measurement.diastolic_mmhg ?? ''))
        setPulse(measurement.pulse_bpm != null ? String(measurement.pulse_bpm) : '')
      } else {
        setSystolic('')
        setDiastolic('')
        setPulse('')
      }
      if (measurement.measurement_type === 'heart_rate') {
        setBpm(String(measurement.bpm ?? ''))
      } else {
        setBpm('')
      }
    } else {
      const now = new Date()
      setMeasuredAt(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16))
      setMeasurementType(defaultType)
      setNotes('')
      setWeight('')
      setWeightUnit('kg')
      setSystolic('')
      setDiastolic('')
      setPulse('')
      setBpm('')
    }
  }, [measurement, open, defaultType])

  const toIso = (localDt: string) => new Date(localDt).toISOString()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const isoMeasured = measuredAt ? toIso(measuredAt) : undefined

      if (measurement) {
        const update: UpdateMeasurementData = {
          measurement_id: measurement.id,
          measured_at: isoMeasured,
          notes,
        }
        if (measurement.measurement_type === 'body_weight') {
          if (!weight.trim()) {
            toast.error('Weight is required')
            setIsSubmitting(false)
            return
          }
          update.weight = weight.trim()
          update.weight_unit = weightUnit
        } else if (measurement.measurement_type === 'blood_pressure') {
          const s = parseInt(systolic, 10)
          const d = parseInt(diastolic, 10)
          if (Number.isNaN(s) || Number.isNaN(d)) {
            toast.error('Valid systolic and diastolic values are required')
            setIsSubmitting(false)
            return
          }
          update.systolic_mmhg = s
          update.diastolic_mmhg = d
          if (pulse.trim()) {
            const p = parseInt(pulse, 10)
            if (!Number.isNaN(p)) update.pulse_bpm = p
          }
        } else if (measurement.measurement_type === 'heart_rate') {
          const hr = parseInt(bpm, 10)
          if (Number.isNaN(hr)) {
            toast.error('Heart rate (bpm) is required')
            setIsSubmitting(false)
            return
          }
          update.bpm = hr
        }
        await onUpdate(update)
        toast.success('Measurement updated')
      } else {
        const create: CreateMeasurementData = {
          measurement_type: measurementType,
          measured_at: isoMeasured,
          notes: notes || undefined,
        }
        if (measurementType === 'body_weight') {
          if (!weight.trim()) {
            toast.error('Weight is required')
            setIsSubmitting(false)
            return
          }
          create.weight = weight.trim()
          create.weight_unit = weightUnit
        } else if (measurementType === 'blood_pressure') {
          const s = parseInt(systolic, 10)
          const d = parseInt(diastolic, 10)
          if (Number.isNaN(s) || Number.isNaN(d)) {
            toast.error('Systolic and diastolic are required')
            setIsSubmitting(false)
            return
          }
          create.systolic_mmhg = s
          create.diastolic_mmhg = d
          if (pulse.trim()) {
            const p = parseInt(pulse, 10)
            if (!Number.isNaN(p)) create.pulse_bpm = p
          }
        } else {
          const hr = parseInt(bpm, 10)
          if (Number.isNaN(hr)) {
            toast.error('Heart rate (bpm) is required')
            setIsSubmitting(false)
            return
          }
          create.bpm = hr
        }
        await onCreate(create)
        toast.success('Measurement recorded')
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  const showTypeSelect = !measurement

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{measurement ? 'Edit measurement' : 'Log measurement'}</DialogTitle>
          <DialogDescription>
            Values are stored in canonical units (kg, mmHg). You can enter weight in lb if you prefer.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {showTypeSelect ? (
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={measurementType}
                onValueChange={(v) => setMeasurementType(v as MeasurementTypeFilter)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="body_weight">Body weight</SelectItem>
                  <SelectItem value="blood_pressure">Blood pressure</SelectItem>
                  <SelectItem value="heart_rate">Heart rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="measurement-when">When</Label>
            <Input
              id="measurement-when"
              type="datetime-local"
              value={measuredAt}
              onChange={(e) => setMeasuredAt(e.target.value)}
              required
            />
          </div>

          {(measurement ? measurement.measurement_type === 'body_weight' : measurementType === 'body_weight') && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="measurement-weight">Weight</Label>
                  <Input
                    id="measurement-weight"
                    type="number"
                    step="any"
                    min={0}
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select value={weightUnit} onValueChange={(v) => setWeightUnit(v as 'kg' | 'lb')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="lb">lb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {(measurement ? measurement.measurement_type === 'blood_pressure' : measurementType === 'blood_pressure') && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="measurement-sys">Systolic (mmHg)</Label>
                <Input
                  id="measurement-sys"
                  type="number"
                  inputMode="numeric"
                  value={systolic}
                  onChange={(e) => setSystolic(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="measurement-dia">Diastolic (mmHg)</Label>
                <Input
                  id="measurement-dia"
                  type="number"
                  inputMode="numeric"
                  value={diastolic}
                  onChange={(e) => setDiastolic(e.target.value)}
                  required
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="measurement-pulse">Pulse (bpm, optional)</Label>
                <Input
                  id="measurement-pulse"
                  type="number"
                  inputMode="numeric"
                  value={pulse}
                  onChange={(e) => setPulse(e.target.value)}
                />
              </div>
            </div>
          )}

          {(measurement ? measurement.measurement_type === 'heart_rate' : measurementType === 'heart_rate') && (
            <div className="space-y-2">
              <Label htmlFor="measurement-bpm">Heart rate (bpm)</Label>
              <Input
                id="measurement-bpm"
                type="number"
                inputMode="numeric"
                value={bpm}
                onChange={(e) => setBpm(e.target.value)}
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="measurement-notes">Notes</Label>
            <Textarea
              id="measurement-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="placeholder:italic"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
